import { useLazyQuery, useQuery } from '@apollo/client'
import React, { createContext, FC, useEffect, useState } from 'react'
import { IQuestion, IQuizResult } from '../components/types'
import {
  createQuizQ1,
  createQuizQ2,
  createQuizQ3,
  createQuizQ4,
  createQuizResult,
} from '../lib/createQuiz'
import { QUIZ_RESULT, QUIZ_SECTIONS } from '../queries/quizQuery'
import {
  quiz_sections,
  quiz_sectionsVariables,
} from '../__generated__/quiz_sections'
import { quiz_result, quiz_resultVariables } from '../__generated__/quiz_result'

interface IQuizContext {
  step: number
  setStep: React.Dispatch<React.SetStateAction<number>>
  final: boolean
  setFinal: React.Dispatch<React.SetStateAction<boolean>>
  q1: IQuestion
  q2: IQuestion
  q3: IQuestion
  q4: IQuestion
  setQuizAnswers: React.Dispatch<React.SetStateAction<number[]>>
  result: IQuizResult
  setResult: React.Dispatch<React.SetStateAction<IQuizResult>>
  loading: boolean
  quizLength: number
}
export const QuizContext = createContext({} as IQuizContext)

const QuizProvider: FC = ({ children }) => {
  const [step, setStep] = useState(1)
  const [final, setFinal] = useState(false)
  const [quizAnswers, setQuizAnswers] = useState<number[]>([])
  const [result, setResult] = useState<IQuizResult>(null)

  const { data } = useQuery<quiz_sections, quiz_sectionsVariables>(
    QUIZ_SECTIONS,
    {
      variables: {
        shadesWhere: {
          iblock: { code: { equals: 'quiz-shades' } },
          level: { equals: 1 },
        },
        typesWhere: {
          iblock: { code: { equals: 'quiz-types' } },
          level: { equals: 1 },
        },
        first: 999,
      },
    },
  )

  useEffect(() => {
    if (quizAnswers.length === 3 && q4?.variants?.length === 1) {
      setFinal(true)
    }
  }, [quizAnswers.length])

  const q1 = createQuizQ1(data.shades)
  const q2 = createQuizQ2(data.shades, quizAnswers[0])
  const q3 = createQuizQ3(data.types)
  const q4 = createQuizQ4(data.types, quizAnswers[2])

  const [getResult, { data: resultData, loading }] = useLazyQuery<
    quiz_result,
    quiz_resultVariables
  >(QUIZ_RESULT, {
    variables: {
      where: {
        parentSection: {
          bx_id: {
            equals:
              quizAnswers[3] || q4?.variants?.length === 1
                ? q4?.variants[0]?.id
                : 297,
          },
        },
        propertyValuesRelation: {
          some: {
            property: { code: { equals: 'OTTENOK_ID' } },
            targetSections: { some: { bx_id: { equals: quizAnswers[1] } } },
          },
        },
      },
    },
  })

  useEffect(() => {
    final && getResult()
  }, [final])

  useEffect(() => {
    if (resultData?.element) {
      setResult(createQuizResult(resultData.element))
    }
  }, [resultData])

  return (
    <QuizContext.Provider
      value={{
        step,
        setStep,
        final,
        setFinal,
        quizLength: quizAnswers.length,
        q1,
        q2,
        q3,
        q4,
        setQuizAnswers,
        loading,
        result,
        setResult,
      }}
    >
      {children}
    </QuizContext.Provider>
  )
}

export default QuizProvider
